import * as React from "react";
import { ToggleSwitch } from "../../toggle-switch/ToggleSwitch.component";
import { useEffect, useState } from "react";
import { createXYZInputs, modifyStringCoordinates, stringCoordinatesToVector3, vector3ToStringCoordinates } from "../ModelSettingsControl.util";
import { BroadcastToPeerMessage, ClippingMeshFacet, RemoveFacetFromModelMessage, RenderTransparencyMessage, RenderVisibilityMessage, RequestVisibilityMessage, RequestVisibilityAnswerMessage, UpdateFacetOnModelMessage, RequestTransparencyMessage, RequestTransparencyAnswerMessage } from "flavr-common";
import { useInteractionModelsStore } from "../../../store/interaction-models.store";
import { modelPool } from "@modules/model";
import { Vector3 } from "flavr-math";
import { useTranslation } from "react-i18next";
const defaultClippingNormal = { x: "1", y: "0", z: "0" };
const defaultClipingPosition = 0.5;
export function ModelSettingsControlAppearance({ connection }) {
    const [modelVisible, setModelVisible] = useState(true);
    const [currentAlpha, setCurrentAlpha] = useState(0);
    const [currentClippingNormal, setCurrentClippingNormal] = useState(defaultClippingNormal);
    const [currentClippingPosition, setCurrentClippingPosition] = useState(defaultClipingPosition);
    const [clipped, setClipped] = useState(false);
    const modelStore = useInteractionModelsStore();
    const { t } = useTranslation();
    useEffect(() => {
        var _a;
        if (modelStore.currentModelUID) {
            connection
                .askByMessageType(new RequestVisibilityMessage(modelStore.currentModelUID), RequestVisibilityAnswerMessage)
                .then((answer) => {
                setModelVisible(answer.visible);
            })
                .catch(() => {
                setModelVisible(false);
            });
            connection
                .askByMessageType(new RequestTransparencyMessage(modelStore.currentModelUID), RequestTransparencyAnswerMessage)
                .then((answer) => {
                setCurrentAlpha(answer.alpha);
            })
                .catch(() => {
                setCurrentAlpha(0);
            });
            const clippingFacet = (_a = modelPool.getModel(modelStore.currentModelUID)) === null || _a === void 0 ? void 0 : _a.getFacet(ClippingMeshFacet);
            if (clippingFacet) {
                setClipped(true);
                const v3 = new Vector3(clippingFacet.planes[0][0], clippingFacet.planes[0][1], clippingFacet.planes[0][2]);
                setCurrentClippingNormal(vector3ToStringCoordinates(v3));
                setCurrentClippingPosition(clippingFacet.planes[0][3]);
            }
            else {
                setCurrentClippingNormal(defaultClippingNormal);
                setCurrentClippingPosition(defaultClipingPosition);
                setClipped(false);
            }
        }
        else {
            setCurrentClippingNormal(defaultClippingNormal);
            setCurrentClippingPosition(defaultClipingPosition);
            setModelVisible(false);
            setCurrentAlpha(0);
            setClipped(false);
        }
    }, [modelStore.currentModelUID]);
    const toggleClipping = () => {
        if (modelStore.currentModelUID) {
            if (!clipped) {
                updateClippingParameters(currentClippingNormal, currentClippingPosition, true);
                setClipped(true);
            }
            else {
                connection.send(new RemoveFacetFromModelMessage(modelStore.currentModelUID, ClippingMeshFacet));
                setCurrentClippingNormal(defaultClippingNormal);
                setCurrentClippingPosition(defaultClipingPosition);
                setClipped(false);
            }
        }
    };
    const toggleVisibility = () => {
        if (modelStore.currentModelUID) {
            const message = new RenderVisibilityMessage(modelStore.currentModelUID, !modelVisible);
            connection.send(message);
            connection.send(new BroadcastToPeerMessage([message]));
            setModelVisible((prev) => !prev);
        }
    };
    const modifyTransparency = (value) => {
        if (modelStore.currentModelUID) {
            const safeValue = isNaN(value) ? 0 : value;
            const message = new RenderTransparencyMessage(modelStore.currentModelUID, safeValue);
            connection.send(message);
            connection.send(new BroadcastToPeerMessage([message]));
            setCurrentAlpha(safeValue);
        }
    };
    const updateClippingParameters = (clipNormal, clipPos, force) => {
        if (modelStore.currentModelUID && (clipped || force)) {
            const v3 = stringCoordinatesToVector3(clipNormal).normalize();
            const n4 = [v3.x, v3.y, v3.z, clipPos];
            const message = new UpdateFacetOnModelMessage(modelStore.currentModelUID, new ClippingMeshFacet([n4]));
            connection.send(message);
            connection.send(new BroadcastToPeerMessage([message]));
        }
    };
    const modifyClippingNormal = (value) => {
        const newNormal = modifyStringCoordinates(currentClippingNormal, value, defaultClippingNormal);
        updateClippingParameters(newNormal, currentClippingPosition);
        setCurrentClippingNormal(newNormal);
    };
    const modifyClippingPosition = (value) => {
        const safeValue = isNaN(value) ? 0 : value;
        updateClippingParameters(currentClippingNormal, safeValue);
        setCurrentClippingPosition(safeValue);
    };
    const visibilityOptionVisible = t("objecteditor:switch-visibility-option-visible", "Visible");
    const visibilityOptionHidden = t("objecteditor:switch-visibility-option-hidden", "Hidden");
    const clippingOptionClipped = t("objecteditor:switch-clipping-option-clipped", "Clipped");
    const clippingOptionUnclipped = t("objecteditor:switch-clipping-option-unclipped", "Unclipped");
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-visibility", "Visibility"))),
            React.createElement(ToggleSwitch, { option1: visibilityOptionVisible, option2: visibilityOptionHidden, selected: modelVisible ? visibilityOptionVisible : visibilityOptionHidden, onChange: toggleVisibility })),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-transparency", "Transparency"))),
            React.createElement("div", null,
                React.createElement("input", { type: "range", min: "0", max: "1", step: "0.05", value: currentAlpha, onChange: (event) => {
                        modifyTransparency(parseFloat(event.target.value));
                    }, disabled: !modelVisible }),
                React.createElement("span", { style: { minWidth: "32px" } }, currentAlpha.toFixed(2)))),
        React.createElement("div", { className: "model-settings-control-row-separator" }),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-clipping", "Clipping"))),
            React.createElement(ToggleSwitch, { option1: clippingOptionClipped, option2: clippingOptionUnclipped, selected: clipped ? clippingOptionClipped : clippingOptionUnclipped, onChange: toggleClipping })),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-clipping-normal", "Clipping Normal"))),
            createXYZInputs(currentClippingNormal, modifyClippingNormal, {
                stepSize: 0.1,
                tooltip: (axis) => t("objecteditor:input-clipping-normal-tooltip", "Specify the {{axis}}-value of the clipping normal. (Must be a value between -1 and 1)\n\nX, Y and Z must be a vector of length 1. If the length is not 1, the vector will be normalized in background.", { axis: axis }),
                tooltipPosition: "top-left",
                disabled: !clipped,
                range: { min: -1, max: 1 }
            })),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-cipping-position", "Clipping Position"))),
            React.createElement("div", null,
                React.createElement("input", { type: "range", min: "-1", max: "1", step: "0.01", value: currentClippingPosition !== null && currentClippingPosition !== void 0 ? currentClippingPosition : "0", onChange: (event) => {
                        modifyClippingPosition(parseFloat(event.target.value));
                    }, disabled: !clipped }),
                React.createElement("span", { style: { minWidth: "32px" } }, currentClippingPosition.toFixed(2))))));
}
